import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';

import { isEmptyString } from 'Shared/helpers';
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';
import { createFeedback, updateFeedback, destroyFeedback } from 'Shared/actions/Feedbacks.actions';
import { FeedbackRecognitionRequestTypes, FeedbackRecognitionTemplateTypes } from 'Shared/constants/types';
import { getFeedbackByUuid, getDraftFeedbackByWorkspaceUuid } from 'Shared/selectors/Feedbacks.selectors';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { videoDecorator, reviewFormTemplateErrors } from './utils';
import RouteChangeWarningModal from 'Shared/components/RouteChangeWarningModal/RouteChangeWarningModal';
import { getWorkspaceTeammate } from 'Shared/selectors/workspace';
import { useReviewTemplate } from 'Shared/utils/hooks/use-review-template';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

import FeedbackCreationHeader from './FeedbackCreationHeader/FeedbackCreationHeader';
import FeedbackTeammateInfo from './FeedbackTeammateInfo/FeedbackTeammateInfo';
import FeedbackRequestSection from './FeedbackRequestSection/FeedbackRequestSection';

import './FeedbackFormSection.scss';

const prefilledValues = {
  wishThatText: 'I wish that you',
  likeThatText: 'I like that you',
};

type Props = {
  templateType: FeedbackRecognitionTemplateTypes;
  children: React.ReactNode;
  errorMessage?: React.ReactNode;
  showRequestFeedback?: boolean;
  allowSaveAsDraft?: boolean;
  handleNext?: () => void;
  handleBack?: () => void;
  values?: {
    likeThatText: string;
  };
};

function FeedbackFormSection({
  templateType,
  children,
  showRequestFeedback = true,
  allowSaveAsDraft = true,
  handleNext,
  handleBack,
  values,
  errorMessage,
}: Props) {
  const { workspaceId, feedbackRecognitionRequestId, feedbackUuid } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState({ isDraftSaving: false, isSubmitting: false });

  const { id: currentUserId } = useCurrentUser();
  const feedback = useDeepEqualSelector(getFeedbackByUuid(feedbackUuid));

  const teammateId = useSelector(state => getWorkspaceTeammate(workspaceId)(state)?.id);

  const { pathname, search, state = {} } = useLocation();
  const { userSuggestionId } = state;
  const { review_template_slug: reviewTemplateSlugFromParams } = queryString.parse(search);

  const reviewTemplateSlug = feedback?.reviewTemplateSlug || reviewTemplateSlugFromParams;

  const isEditFeedback = pathname.includes('edit');
  const isCreateFeedback = !isEditFeedback;

  const isBlankTemplateFeedback = templateType === FeedbackRecognitionTemplateTypes.feedbackBlankTemplate;

  const draftFeedback = useDeepEqualSelector(getDraftFeedbackByWorkspaceUuid(workspaceId));

  const isFeedbackDraft = draftFeedback?.uuid === feedbackUuid;

  const { reviewFormTemplates } = useReviewTemplate({ slug: reviewTemplateSlug, withFetch: false });

  const handleFormSubmit = async ({
    wishThatText,
    wishThatVideo,
    likeThatText,
    likeThatVideo,
    answers,
    saveDraftClicked,
    isFeedbackRequested,
  }) => {
    if (
      templateType === FeedbackRecognitionTemplateTypes.feedbackfromReviewTemplateTemplate &&
      reviewFormTemplates &&
      !saveDraftClicked
    ) {
      const answerErrors = reviewFormTemplateErrors({ reviewFormTemplate: reviewFormTemplates[0], answers });
      if (answerErrors.length > 0) return { answers: answerErrors };
    }

    const perform = isEditFeedback ? updateFeedback : createFeedback;
    if (saveDraftClicked) {
      setSubmitting({ ...submitting, isDraftSaving: true });
    } else {
      setSubmitting({ ...submitting, isSubmitting: true });
    }

    const likeThatTextValue = () => {
      if (isCreateFeedback && !isBlankTemplateFeedback && likeThatText === prefilledValues.likeThatText) return null;
      return likeThatText;
    };

    const wishThatTextValue = () => {
      if (isBlankTemplateFeedback) return null;
      if (isCreateFeedback && wishThatText === prefilledValues.wishThatText) return null;
      return wishThatText;
    };

    const paramsQuery = {
      uuid: feedback?.uuid || uuidv4(),
      creatorId: currentUserId,
      receiverId: teammateId,
      feedbackRecognitionRequestId,
      requestType: FeedbackRecognitionRequestTypes.feedback,
      workspaceId,
      likeThatText: likeThatTextValue(),
      likeThatVideo,
      wishThatText: wishThatTextValue(),
      wishThatVideo: isBlankTemplateFeedback ? null : wishThatVideo,
      answers,
      reviewTemplateSlug,
      template_type: templateType,
      isDraft: saveDraftClicked,
      isFeedbackRequested,
      userSuggestionId: !saveDraftClicked && userSuggestionId,
    };
    const data = isEditFeedback
      ? omit(paramsQuery, [
          'creatorId',
          'receiverId',
          'feedbackRecognitionRequestId',
          'requestType',
          'workspaceId',
          'reviewTemplateSlug',
        ])
      : paramsQuery;

    dispatch(perform(data))
      .then(() => history.push(PageUrlsUtil.makeWorkspaceFeedbackUrl(workspaceId)))
      .finally(() => setSubmitting({ isDraftSaving: false, isSubmitting: false }));
  };

  const handleDeleteDraftClick = () => {
    dispatch(destroyFeedback(feedback.uuid)).then(() =>
      history.push(PageUrlsUtil.makeWorkspaceFeedbackUrl(workspaceId))
    );
  };

  const handleSaveAsDraftClick = form => {
    form.change('saveDraftClicked', true);
  };

  const handleSubmitFeedbackClick = form => form.change('saveDraftClicked', false);

  const isEmptyValues = values => {
    return (
      isEmpty(values.likeThatVideo) &&
      isEmpty(values.wishThatVideo) &&
      isEmptyString(values.likeThatText) &&
      isEmptyString(values.wishThatText) &&
      isEmpty(values.answers)
    );
  };

  const isPristineInitValues = values => {
    return (
      isCreateFeedback &&
      isEmpty(values.likeThatVideo) &&
      isEmpty(values.wishThatVideo) &&
      values.likeThatText === prefilledValues.likeThatText &&
      values.wishThatText === prefilledValues.wishThatText &&
      isEmpty(values.answers)
    );
  };

  const initialValues = () => {
    if (isEditFeedback || isBlankTemplateFeedback) {
      return {
        ...feedback,
        likeThatVideo: videoDecorator(feedback?.likeThatVideo),
        wishThatVideo: videoDecorator(feedback?.wishThatVideo),
        isFeedbackRequested: false,
      };
    }

    return {
      wishThatText: prefilledValues.wishThatText,
      likeThatText: prefilledValues.likeThatText,
      likeThatVideo: null,
      wishThatVideo: null,
      isFeedbackRequested: false,
      ...values,
    };
  };

  const showRequestFeedbackSection =
    (showRequestFeedback && !isEditFeedback) || (showRequestFeedback && isEditFeedback && isFeedbackDraft);

  const formMutators = {
    replaceTextMutator: (args, state, utils) => {
      utils.changeValue(state, args[0], () => args[1]);
    },
  };

  return (
    <div className="FeedbackFormSection">
      <Form onSubmit={handleFormSubmit} initialValues={initialValues()} mutators={{ ...formMutators }}>
        {({ handleSubmit, form, pristine, values }) => (
          <form onSubmit={handleSubmit}>
            <FeedbackCreationHeader
              isEditFeedback={isEditFeedback}
              isSubmitDisabled={isEmptyValues(values) || isPristineInitValues(values)}
              pristine={pristine}
              submitting={submitting}
              onDeleteDraftClick={feedback?.isDraft ? handleDeleteDraftClick : null}
              onSaveAsDraftClick={() => handleSaveAsDraftClick(form)}
              onSubmitFeedbackClick={() => handleSubmitFeedbackClick(form)}
              templateType={templateType}
              isFeedbackRequested={values.isFeedbackRequested}
              hasVideo={Boolean(values.likeThatVideo) || Boolean(values.wishThatVideo)}
              allowSaveAsDraft={allowSaveAsDraft}
              handleNext={handleNext}
              handleBack={handleBack}
            />
            <div className="px-3">
              <div className="FeedbackFormSection__content pt-5">
                {errorMessage && <div className="FeedbackFormSection__content__error-message">{errorMessage}</div>}
                <FeedbackTeammateInfo isEditFeedback={isEditFeedback} />

                {children}

                {showRequestFeedbackSection && (
                  <div className="mt-4">
                    <FeedbackRequestSection />
                  </div>
                )}
              </div>
            </div>
            <RouteChangeWarningModal when={!pristine && !submitting.isDraftSaving && !submitting.isSubmitting}>
              <p>Leave without submitting feedback?</p>
              <p className="mb-0">
                <b>Your feedback will be lost.</b>
              </p>
            </RouteChangeWarningModal>
          </form>
        )}
      </Form>
    </div>
  );
}

export default FeedbackFormSection;
